import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { apiClient, errorMessage, unwrap } from '../lib/api'
import { mixSum } from '../lib/weightedMix'
import { queryKeys, useFormats, useImageStyles, useSettings } from '../lib/queries'
import type { AccountSettings } from '../lib/postTypes'
import { useAuth } from '../contexts/AuthContext'
import { ErrorBox } from '../components/QueryState'
import { WritingSection } from './settings/WritingSection'
import { ImageSection } from './settings/ImageSection'
import { NotificationSection } from './settings/NotificationSection'
import { GuardSection } from './settings/GuardSection'

const SECTIONS = [
  { key: 'writing', label: '執筆スタイル' },
  { key: 'image', label: '画像生成' },
  { key: 'notification', label: '通知' },
  { key: 'guard', label: '安全チェック' },
] as const
type SectionKey = (typeof SECTIONS)[number]['key']

/** draft の元になったサーバー値（どのアカウントの・どの取得結果か） */
interface Seed {
  accountId: string
  data: AccountSettings
  /** 再シードのたびに増やし、ローカル state を持つセクションを作り直す key にする */
  rev: number
}

export function Settings() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const accountId = user?.accountId ?? ''
  const [section, setSection] = useState<SectionKey>('writing')
  const [draft, setDraft] = useState<AccountSettings | null>(null)
  const [seed, setSeed] = useState<Seed | null>(null)
  const [saved, setSaved] = useState(false)

  const settings = useSettings()
  const formats = useFormats()
  const imageStyles = useImageStyles()

  // サーバー値をローカル編集用にコピー（保存前の編集は draft に持つ）。
  // 従来は「最初の1回だけ」コピーしていたため、アカウントが変わっても前のアカウントの
  // 設定が draft に残り、そのまま保存し得た（audit C9）。
  // - アカウントが変わった → 必ず作り直す
  // - 再取得で値が変わった → 未編集のときだけ作り直す（編集中の内容は消さない）
  useEffect(() => {
    const data = settings.data
    if (!data) return
    const accountChanged = seed?.accountId !== accountId
    if (seed && !accountChanged && seed.data === data) return
    const untouched = !draft || !seed || JSON.stringify(draft) === JSON.stringify(seed.data)
    if (accountChanged || untouched) {
      setDraft(structuredClone(data))
      setSeed({ accountId, data, rev: (seed?.rev ?? 0) + 1 })
    }
    // draft/seed は判定に使うだけ（それらの変化のたびに走らせる必要はない）
  }, [settings.data, accountId])

  const save = useMutation({
    mutationFn: async () => unwrap(await apiClient.patch<AccountSettings>('/settings', draft)),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.settings, data)
      setDraft(structuredClone(data))
      setSeed(s => ({ accountId, data, rev: (s?.rev ?? 0) + 1 }))
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  const heading = <h1 className="text-2xl font-bold leading-snug">設定</h1>

  if (!draft || !seed) {
    if (settings.isError) {
      return (
        <div className="space-y-4">
          {heading}
          <ErrorBox message={`設定の読み込みに失敗しました: ${errorMessage(settings.error)}`}
            onRetry={() => { void settings.refetch() }} />
        </div>
      )
    }
    return (
      <div className="space-y-4">
        {heading}
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 size={16} className="text-primary motion-safe:animate-spin" aria-hidden /> 読み込み中…
        </p>
      </div>
    )
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(settings.data)
  const formatMixSum = mixSum(draft.writing.formatMix)
  const mixInvalid = draft.writing.formatMix.length > 0 && formatMixSum !== 100
  const styleMixSum = mixSum(draft.image.styleMix ?? [])
  const styleMixInvalid = (draft.image.styleMix ?? []).length > 0 && styleMixSum !== 100

  return (
    <div className="space-y-4">
      {heading}
      <div className="flex flex-col gap-4 md:flex-row md:gap-6">
        {/* セクションメニュー（狭い幅では横に折り返す） */}
        <nav className="flex flex-wrap gap-1 md:w-44 md:shrink-0 md:flex-col" data-testid="settings-sidebar">
          {SECTIONS.map(s => (
            <button key={s.key} onClick={() => setSection(s.key)}
              aria-current={section === s.key ? 'true' : undefined}
              className={`block rounded-ctl border-l-2 px-3 py-2 text-left text-sm md:w-full ${
                section === s.key ? 'border-primary bg-surface-2 font-bold text-ink' : 'border-transparent text-ink-muted hover:bg-surface-2 hover:text-ink'
              }`}>
              {s.label}
            </button>
          ))}
        </nav>

        {/* 本体 */}
        <div className="min-w-0 flex-1 space-y-4" key={seed.rev}>
          {section === 'writing' && (
            <>
              {formats.isError && (
                <ErrorBox message={`投稿の型の一覧を読み込めませんでした: ${errorMessage(formats.error)}`}
                  onRetry={() => { void formats.refetch() }} />
              )}
              <WritingSection draft={draft} setDraft={setDraft} formats={formats.data ?? []} />
            </>
          )}
          {section === 'image' && (
            <>
              {imageStyles.isError && (
                <ErrorBox message={`画像スタイルの一覧を読み込めませんでした: ${errorMessage(imageStyles.error)}`}
                  onRetry={() => { void imageStyles.refetch() }} />
              )}
              <ImageSection draft={draft} setDraft={setDraft} styles={imageStyles.data ?? []} />
            </>
          )}
          {section === 'notification' && (
            <NotificationSection draft={draft} setDraft={setDraft} />
          )}
          {section === 'guard' && (
            <GuardSection draft={draft} setDraft={setDraft} />
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button disabled={!dirty || mixInvalid || styleMixInvalid || save.isPending} onClick={() => save.mutate()}
              className="gm-btn gm-btn-primary">
              {save.isPending ? '保存中…' : '保存する'}
            </button>
            {mixInvalid && <span className="text-sm text-warn">型の配分の合計を 100% にしてください（現在 <span className="gm-num">{formatMixSum}</span>%）</span>}
            {styleMixInvalid && <span className="text-sm text-warn">画像スタイルの配分の合計を 100% にしてください（現在 <span className="gm-num">{styleMixSum}</span>%）</span>}
            {saved && <span className="text-sm text-ok">保存しました</span>}
            {save.isError && <span role="alert" className="text-sm text-bad">{errorMessage(save.error)}</span>}
          </div>
        </div>
      </div>
    </div>
  )
}
